import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';

const SolutionSection = () => {
  const [activeCard, setActiveCard] = useState(0);
  const [headerVisible, setHeaderVisible] = useState(false);
  const [featuresVisible, setFeaturesVisible] = useState([]);
  const [ctaVisible, setCtaVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const headerObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) { setHeaderVisible(true); headerObserver.disconnect(); }
      },
      { threshold: 0.2 }
    );
    const ctaObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) { setCtaVisible(true); ctaObserver.disconnect(); }
      },
      { threshold: 0.3 }
    );
    const featureObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          [0, 1, 2, 3].forEach((i) => {
            setTimeout(() => setFeaturesVisible(prev => [...prev, i]), i * 120);
          });
          featureObserver.disconnect();
        }
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) {
      const headerEl = sectionRef.current.querySelector('#sol-header');
      const ctaEl = sectionRef.current.querySelector('#sol-cta');
      const featureEl = sectionRef.current.querySelector('#sol-features');
      if (headerEl) headerObserver.observe(headerEl);
      if (ctaEl) ctaObserver.observe(ctaEl);
      if (featureEl) featureObserver.observe(featureEl);
    }
    return () => { headerObserver.disconnect(); ctaObserver.disconnect(); featureObserver.disconnect(); };
  }, []);

  const transformationPairs = [
    {
      id: 1,
      before: {
        thought: "I'm terrible at presentations. Everyone will judge me.",
        emotion: "The old loop",
        icon: "Frown"
      },
      after: {
        thought: "This is an opportunity to share my ideas and connect with others.",
        emotion: "The replacement",
        icon: "Smile"
      },
      technique: "Mind Reframing",
      description: "DepthMe identifies the thought pattern and guides you to build a believable replacement — one that your brain can actually accept."
    },
    {
      id: 2,
      before: {
        thought: "I always mess up important relationships.",
        emotion: "The old loop",
        icon: "Frown"
      },
      after: {
        thought: "I'm learning and growing through each relationship experience.",
        emotion: "The replacement",
        icon: "Heart"
      },
      technique: "Your Voice Makes It Real",
      description: "You record yourself saying the new thought. Hearing it in your own voice makes it familiar — the brain learns through repetition in a voice it trusts."
    },
    {
      id: 3,
      before: {
        thought: "I don't deserve success or happiness.",
        emotion: "The old loop",
        icon: "CloudRain"
      },
      after: {
        thought: "I am worthy of good things and capable of creating positive change.",
        emotion: "The replacement",
        icon: "Sun"
      },
      technique: "Daily Rituals Reinforce It",
      description: "Personalized guidance built around your real situation, repeated daily until the new thought becomes your default."
    }
  ];

  const features = [
    {
      icon: "Brain",
      title: "Mind Reframing",
      description: "Replace negative loops by installing new thoughts that feel believable in your own voice"
    },
    {
      icon: "Mic",
      title: "Your Voice as Guide",
      description: "DepthMe generates your daily guidance in your own cloned voice — familiar, trustworthy, effective"
    },
    {
      icon: "Calendar",
      title: "Four Daily Rituals",
      description: "Morning Intentions, Shadow Work, Vision Quest, Night Reflections — a complete daily practice"
    },
    {
      icon: "Shield",
      title: "Zero-Knowledge Privacy",
      description: "Your entries are encrypted before they leave your device. Not even we can read them"
    }
  ];

  return (
    <section id="solution" ref={sectionRef} className="py-20 px-6">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div id="sol-header" className={`text-center mb-16 transition-all duration-1000 ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
            You don't change by{' '}
            <span className="text-gradient">fighting thoughts.</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            You change by replacing them — through mind reframing, your own voice, and daily rituals
            that repeat the new thought until it becomes your default.
          </p>
        </div>

        {/* Transformation Cards + Image2 Split */}
        <div className="mb-16 md:grid md:grid-cols-3 md:gap-8 md:items-start">
          {/* Image2 accent panel (desktop only) */}
          <div className="hidden md:block relative rounded-3xl overflow-hidden h-full min-h-[420px]">
            <img
              src="/image2.jpg"
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover object-center opacity-50"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-background/60 to-accent/10" />
            <div className="relative z-10 flex flex-col items-center justify-center h-full p-8 text-center">
              <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center mb-4">
                <Icon name="Sparkles" size={28} className="text-primary" />
              </div>
              <p className="text-lg font-semibold text-foreground leading-snug">AI-guided transformation in minutes a day</p>
            </div>
          </div>
          <div className="md:col-span-2">
            <h3 className="text-2xl font-semibold text-foreground text-center mb-8">
              How one thought gets replaced
            </h3>
            
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              {/* Before Card */}
              <div className="glassmorphism p-8 rounded-3xl border-l-4 border-error/50">
                <div className="flex items-center mb-4">
                  <Icon name="X" size={20} className="text-error mr-2" />
                  <span className="text-sm font-medium text-error uppercase tracking-wide">Before</span>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <Icon name={transformationPairs?.[activeCard]?.before?.icon} size={24} className="text-error mt-1" />
                    <div>
                      <blockquote className="text-lg text-foreground italic mb-2">
                        "{transformationPairs?.[activeCard]?.before?.thought}"
                      </blockquote>
                      <p className="text-sm text-error font-medium">
                        {transformationPairs?.[activeCard]?.before?.emotion}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* After Card */}
              <div className="glassmorphism p-8 rounded-3xl border-l-4 border-accent/50">
                <div className="flex items-center mb-4">
                  <Icon name="Check" size={20} className="text-accent mr-2" />
                  <span className="text-sm font-medium text-accent uppercase tracking-wide">After</span>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <Icon name={transformationPairs?.[activeCard]?.after?.icon} size={24} className="text-accent mt-1" />
                    <div>
                      <blockquote className="text-lg text-foreground italic mb-2">
                        "{transformationPairs?.[activeCard]?.after?.thought}"
                      </blockquote>
                      <p className="text-sm text-accent font-medium">
                        {transformationPairs?.[activeCard]?.after?.emotion}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Technique Explanation */}
            <div className="glassmorphism p-6 rounded-2xl text-center">
              <div className="flex items-center justify-center space-x-2 mb-3">
                <Icon name="Lightbulb" size={20} className="text-primary" />
                <span className="font-semibold text-primary">
                  {transformationPairs?.[activeCard]?.technique}
                </span>
              </div>
              <p className="text-muted-foreground">
                {transformationPairs?.[activeCard]?.description}
              </p>
            </div>

            {/* Card Navigation */}
            <div className="flex justify-center space-x-2 mt-6">
              {transformationPairs?.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveCard(index)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    index === activeCard
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  Example {index + 1}
                </button>
              ))}
            </div>
          </div>{/* end md:col-span-2 */}
        </div>

        {/* Features Grid */}
        <div id="sol-features" className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features?.map((feature, index) => (
            <div
              key={index}
              className={`glassmorphism p-6 rounded-2xl text-center group hover:violet-glow transition-all duration-700 ${
                featuresVisible?.includes(index) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
            >
              <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-primary/20 flex items-center justify-center group-hover:bg-primary/30 transition-colors">
                <Icon name={feature?.icon} size={24} className="text-primary" />
              </div>
              <h4 className="text-lg font-semibold text-foreground mb-2">
                {feature?.title}
              </h4>
              <p className="text-sm text-muted-foreground">
                {feature?.description}
              </p>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div id="sol-cta" className={`text-center mt-16 transition-all duration-1000 ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="glassmorphism p-8 rounded-3xl max-w-2xl mx-auto">
            <Icon name="Repeat" size={48} className="text-accent mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-foreground mb-4">
              New thoughts, new attitude, new life.
            </h3>
            <p className="text-muted-foreground mb-6">
              The more you do it, the more it becomes a habit. Small daily rituals — real change.
            </p>
            <div className="flex items-center justify-center space-x-2 text-sm text-accent">
              <Icon name="Clock" size={16} />
              <span>5–10 minutes per ritual</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SolutionSection;